import { PaymentRetentionItem } from '@turizi/shared';

export interface RetentionRatePreset {
    labelKey?: string;
    label: string;
    percentage: number;
    jurisdiction?: string;
}

export interface ArgentinaJurisdictionRetention {
    code: string;
    name: string;
    defaultRate: number;
    commonRates: number[];
}

/**
 * Standard Ingresos Brutos (IIBB) withholding rates by Argentine province / jurisdiction.
 */
export const ARGENTINA_IIBB_JURISDICTIONS: ArgentinaJurisdictionRetention[] = [
    { code: 'RN', name: 'Río Negro', defaultRate: 1.0, commonRates: [1.5, 2.0, 3.0, 3.5] },
    { code: 'NQN', name: 'Neuquén', defaultRate: 2.0, commonRates: [1.5, 2.0, 3.0, 4.0] },
    { code: 'CABA', name: 'CABA (AGIP)', defaultRate: 3.0, commonRates: [1.5, 2.0, 3.0, 4.0, 4.5] },
    { code: 'BA', name: 'Buenos Aires (ARBA)', defaultRate: 2.5, commonRates: [1.75, 2.5, 3.0, 3.5, 4.0] },
    { code: 'CBA', name: 'Córdoba', defaultRate: 3.0, commonRates: [1.5, 2.5, 3.0, 4.0] },
    { code: 'SF', name: 'Santa Fe (API)', defaultRate: 2.5, commonRates: [1.5, 2.5, 3.5, 4.5] },
    { code: 'MZA', name: 'Mendoza (ATM)', defaultRate: 2.5, commonRates: [2.0, 2.5, 3.0, 3.5] },
    { code: 'SLA', name: 'Salta (DGR)', defaultRate: 3.6, commonRates: [1.8, 3.6, 5.0] },
    { code: 'SC', name: 'Santa Cruz', defaultRate: 2.0, commonRates: [1.5, 2.0, 3.0] },
    { code: 'TDF', name: 'Tierra del Fuego (AREF)', defaultRate: 2.5, commonRates: [1.5, 2.5, 3.0] },
    { code: 'CHU', name: 'Chubut', defaultRate: 2.0, commonRates: [1.5, 2.0, 3.0] },
    { code: 'MIS', name: 'Misiones (ATM)', defaultRate: 3.31, commonRates: [2.5, 3.31, 4.5] },
    { code: 'TUC', name: 'Tucumán', defaultRate: 3.5, commonRates: [2.5, 3.5, 5.0] },
    { code: 'ER', name: 'Entre Ríos (ATER)', defaultRate: 3.0, commonRates: [2.0, 3.0, 4.0] },
    { code: 'COR', name: 'Corrientes', defaultRate: 2.5, commonRates: [2.0, 2.5, 3.5] },
    { code: 'JUJ', name: 'Jujuy', defaultRate: 3.0, commonRates: [2.0, 3.0, 3.5] },
    { code: 'CHA', name: 'Chaco (ATP)', defaultRate: 3.5, commonRates: [2.5, 3.5, 4.5] },
    { code: 'SJ', name: 'San Juan', defaultRate: 2.5, commonRates: [2.0, 2.5, 3.0] },
    { code: 'SL', name: 'San Luis', defaultRate: 3.0, commonRates: [2.0, 3.0, 4.0] },
    { code: 'CAT', name: 'Catamarca', defaultRate: 2.5, commonRates: [2.0, 2.5, 3.5] },
    { code: 'LR', name: 'La Rioja', defaultRate: 2.5, commonRates: [2.0, 2.5, 3.5] },
    { code: 'LP', name: 'La Pampa', defaultRate: 2.5, commonRates: [2.0, 2.5, 3.0] },
    { code: 'SDE', name: 'Santiago del Estero', defaultRate: 3.0, commonRates: [2.0, 3.0, 4.0] },
    { code: 'FOR', name: 'Formosa', defaultRate: 3.0, commonRates: [2.0, 3.0, 3.5] },
];

/**
 * Default percentage when selecting a retention type.
 */
export const DEFAULT_RETENTION_PERCENTAGE_BY_TYPE: Record<PaymentRetentionItem['type'], number> = {
    iibb: 3.0,
    ganancias: 2.0,
    iva: 10.5,
    suss: 1.0,
    other: 0,
};

/**
 * Standard Argentine AFIP/ARCA & Provincial withholding percentage presets per tax type.
 */
export const RETENTION_PERCENTAGE_PRESETS: Record<PaymentRetentionItem['type'], RetentionRatePreset[]> = {
    ganancias: [
        { label: '2% - Locación de servicios / Compras (RG 830)', percentage: 2.0 },
        { label: '0.5% - Transporte de pasajeros / cargas (RG 830)', percentage: 0.5 },
        { label: '6% - Alquileres / Honorarios (RG 830)', percentage: 6.0 },
        { label: '28% - Sujeto No Inscripto (RG 830)', percentage: 28.0 },
    ],
    iibb: [
        { label: '1.5% - IIBB Alícuota Reducida', percentage: 1.5 },
        { label: '2.0% - IIBB Patagónicas / Reducida', percentage: 2.0 },
        { label: '2.5% - IIBB ARBA / Santa Fe / Mendoza', percentage: 2.5 },
        { label: '3.0% - IIBB CABA / Córdoba / General', percentage: 3.0 },
        { label: '3.31% - IIBB Misiones', percentage: 3.31 },
        { label: '3.5% - IIBB Tucumán / Chaco / ARBA', percentage: 3.5 },
        { label: '3.6% - IIBB Salta', percentage: 3.6 },
        { label: '4.0% - IIBB Riesgo Fiscal / Alta', percentage: 4.0 },
        { label: '4.5% - IIBB Alícuota Agravada', percentage: 4.5 },
        { label: '5.0% - IIBB No Inscripto / Máxima', percentage: 5.0 },
    ],
    iva: [
        { label: '10.5% - Servicios 50% de IVA 21% (RG 2854)', percentage: 10.5 },
        { label: '8.4% - Servicios 80% de IVA 10.5% (RG 2854)', percentage: 8.4 },
        { label: '16.8% - 80% de IVA 21% (RG 2854)', percentage: 16.8 },
        { label: '21% - 100% IVA (Factura M / Otros)', percentage: 21.0 },
    ],
    suss: [
        { label: '1% - Régimen General Servicios (RG 1784)', percentage: 1.0 },
        { label: '2% - Servicios eventuales', percentage: 2.0 },
        { label: '6% - Limpieza / Seguridad / Construcción', percentage: 6.0 },
    ],
    other: [
        { label: '1%', percentage: 1.0 },
        { label: '1.2% - Imp. Débitos/Créditos', percentage: 1.2 },
        { label: '2%', percentage: 2.0 },
        { label: '3%', percentage: 3.0 },
        { label: '5%', percentage: 5.0 },
    ],
};

/**
 * Helper to compute retention amount from a base total amount and a percentage.
 */
export function calculateRetentionAmount(baseAmount: number, percentage: number): number {
    if (!baseAmount || !percentage || baseAmount <= 0 || percentage <= 0) {
        return 0;
    }
    return Number(((baseAmount * percentage) / 100).toFixed(2));
}

